import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  createStyles,
  makeStyles,
  Box,
  Theme,
  Typography,
} from '@material-ui/core';
import { Property, Survey } from '../../types';
import { BHPropertyFieldRule } from '../../types/bh-property-field-rule';
import { getPropertyValueForDisplay } from '../../utilities/property';
import { renderLinkFieldValue, getColumnHeader } from '../../utilities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0 20px',
      '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        gap: '0',
      },
    },
    gridRowWrapper: {
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: '40% minmax(0, 1fr)',
      gap: '0 20px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: '0',
      marginBottom: '0',
      '&:last-child': {
        borderBottom: 'none',
      },
      '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        gap: '0',
        paddingBottom: '0',
        marginBottom: '0',
      },
    },
    gridLabel: {
      color: '#778F9C',
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      padding: '12px 0',
      width: '100%',
    },
    gridValue: {
      color: theme.palette.text.primary,
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '20px',
      padding: '12px 0',
      wordBreak: 'break-word',
    },
  }),
);

const normalizeFieldIdentity = (value?: string) =>
  `${value || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');

const InteractivePropertyDetail: React.FC<Props> = ({
  survey,
  property,
  excludedFieldIdentities = [],
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const normalizedExcludedFieldIdentities = excludedFieldIdentities.map(
    normalizeFieldIdentity,
  );
  const ignoreKeys = [
    'address1',
    'address',
    'city',
    'state',
    'postalCode',
    'comments',
    'amenities',
    'propertyProposedSpace',
    'proposedSpace',
  ];
  const activePropertyFieldRuleList = survey?.surveyPropertyFieldRule?.filter(
    (rule: any) =>
      rule.fieldKey !== 'name' &&
      !ignoreKeys.includes(rule.fieldKey) &&
      !normalizedExcludedFieldIdentities.includes(normalizeFieldIdentity(rule.fieldKey)) &&
      !normalizedExcludedFieldIdentities.includes(normalizeFieldIdentity(rule.fieldLabel)) &&
      rule.status === 'ACTIVE',
  );

  const propertyDetailsRows = (activePropertyFieldRuleList || [])
    .map((row: BHPropertyFieldRule) => {
      const attrKey = row.fieldKey;
      const data = property as Property & { [key: string]: any };
      const { customFields } = data;
      let attrValue = '';
      let customField = null;

      if (Array.isArray(customFields) && customFields.length > 0) {
        customField = customFields.find((field) => field.fieldKey === row.fieldKey);
      }

      if (customField && `${customField?.value || ''}` !== '') {
        attrValue = customField.value;
      } else {
        attrValue = getPropertyValueForDisplay(attrKey, data);
      }

      return {
        row,
        attrKey,
        attrValue,
      };
    });

  const displayPropertyFieldRuleRow = (
    row: BHPropertyFieldRule,
    attrValue: string,
  ) => (
    <Box key={row.fieldKey} className={classes.gridRowWrapper}>
      <Box className={classes.gridLabel}>
        <Typography>
          {getColumnHeader(t, i18n.language, row.fieldKey, row.fieldLabel)}
        </Typography>
      </Box>
      <Box className={classes.gridValue}>
        {renderLinkFieldValue({
          value: attrValue,
          fieldLabel: row.fieldLabel,
          surveyCountry: survey.surveyCountry,
          propertyType: survey.propertyType,
        })}
      </Box>
    </Box>
  );

  return (
    <Box className={classes.gridContainer}>
      {propertyDetailsRows.map((item: any) =>
        displayPropertyFieldRuleRow(item.row, item.attrValue),
      )}
    </Box>
  );
};
export type Props = {
  survey: Survey;
  property: Property;
  excludedFieldIdentities?: string[];
};

export default InteractivePropertyDetail;
